/**
 * CalcQuick — Calculadora rápida (versión mini del módulo Calculadora de Precios).
 * Se muestra en un Popover desde el TopMenuBar para que el usuario haga cálculos
 * sin abandonar la página en la que se encuentra.
 *
 * Comparte la fórmula con /app/frontend/src/pages/TraveldeskCalculadora.jsx
 */
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RotateCcw, ExternalLink, DollarSign, User } from "lucide-react";

const parseFloatSafe = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const fmt = (n) => Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CANALES = [
  { key: "BTRAVEL", label: "BTRAVEL", comisionDefault: 20 },
  { key: "GRUPOS",  label: "GRUPOS",  comisionDefault: 20 },
  { key: "DIRECTO", label: "DIRECTO", comisionDefault: 15 },
];
const PAGOS = [
  { key: "SPEI",  label: "SPEI" },
  { key: "TDC1X", label: "1x TDC" },
  { key: "MSI3",  label: "3 MSI" },
  { key: "MSI6",  label: "6 MSI" },
];

export default function CalcQuick({ triggerClassName = "" }) {
  const [open, setOpen] = useState(false);
  const [tcSistema, setTcSistema] = useState({ usd: null, eur: null });

  const [gastoEnUSD, setGastoEnUSD] = useState(false);
  const [tcProveedor, setTcProveedor] = useState("");
  const [base, setBase] = useState("");
  const [tax, setTax] = useState("");
  const [comision, setComision] = useState(20);
  const [autoComision, setAutoComision] = useState(false);
  const [canal, setCanal] = useState("BTRAVEL");
  const [pago, setPago] = useState("SPEI");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/tipo-cambio`);
        setTcSistema(r.data || {});
      } catch {
        toast.error("No se pudo cargar TC");
      }
    })();
  }, [open]);

  useEffect(() => {
    if (autoComision) return;
    const c = CANALES.find((x) => x.key === canal);
    if (c) setComision(c.comisionDefault);
  }, [canal, autoComision]);

  // Cálculo PVP MXN (misma fórmula que el módulo)
  const pvpMXN = useMemo(() => {
    const u = parseFloatSafe(comision);
    const d = 1 - (u / 100);
    if (d <= 0) return 0;
    const b = parseFloatSafe(base);
    const t = parseFloatSafe(tax);
    if (gastoEnUSD) {
      const tarifaBaseUSD = b / d;
      const totalPVPusd = tarifaBaseUSD + t;
      const tcUsd = parseFloatSafe(tcSistema.usd);
      return totalPVPusd * tcUsd;
    }
    return (b / d) + t;
  }, [gastoEnUSD, base, tax, comision, tcSistema.usd]);

  const totalGastoMXN = useMemo(() => {
    const suma = parseFloatSafe(base) + parseFloatSafe(tax);
    if (gastoEnUSD) return suma * parseFloatSafe(tcProveedor);
    return suma;
  }, [gastoEnUSD, base, tax, tcProveedor]);

  // Openpay BBVA MX: tasa + $3 + IVA sobre la comisión
  const totalPago = useMemo(() => {
    const b = pvpMXN;
    switch (pago) {
      case "SPEI":  return b;
      case "TDC1X": return b + (b * 0.034 + 3) * 1.16;
      case "MSI3":  return b + (b * 0.049 + 3) * 1.16;
      case "MSI6":  return b + (b * 0.079 + 3) * 1.16;
      default:      return b;
    }
  }, [pvpMXN, pago]);

  const reset = () => {
    setGastoEnUSD(false);
    setTcProveedor(""); setBase(""); setTax("");
    setComision(20); setAutoComision(false);
    setCanal("BTRAVEL"); setPago("SPEI");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`hidden lg:inline-flex items-center justify-center text-cyan-100 bg-white/10 rounded-lg w-7 h-7 border border-white/10 hover:bg-white/20 hover:text-white transition-colors ${triggerClassName}`}
          title="Cotizar precio (venta por persona)"
          data-testid="calc-quick-trigger"
        >
          <span className="relative inline-flex items-center">
            <User className="w-4 h-4" />
            <DollarSign className="w-3.5 h-3.5 -ml-1 -mt-1.5 text-emerald-300" strokeWidth={2.6} />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={10}
        className="w-[360px] p-0 rounded-xl border border-slate-200 shadow-xl overflow-hidden"
        data-testid="calc-quick-popover"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#00157F] to-[#0037FF] px-3.5 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <span className="relative inline-flex items-center">
              <User className="w-4 h-4" />
              <DollarSign className="w-3.5 h-3.5 -ml-1 -mt-1.5 text-emerald-300" strokeWidth={2.6} />
            </span>
            <div className="leading-tight">
              <p className="text-[13px] font-semibold">Cotizador rápido</p>
              <p className="text-[10px] text-cyan-200">
                TC USD {tcSistema.usd ? Number(tcSistema.usd).toFixed(4) : "—"}
              </p>
            </div>
          </div>
          <button
            onClick={() => window.open("/traveldesk/calculadora", "_blank")}
            className="text-cyan-200 hover:text-white transition-colors"
            title="Abrir módulo completo"
            data-testid="calc-quick-open-full"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-3.5 space-y-3 bg-white">
          {/* Moneda */}
          <div className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
            <div>
              <p className="text-xs font-semibold text-slate-700">Gasto en USD</p>
              <p className="text-[10px] text-slate-500">Enciende el check para capturar en USD</p>
            </div>
            <Switch checked={gastoEnUSD} onCheckedChange={setGastoEnUSD} data-testid="calc-quick-usd-switch" />
          </div>

          {/* Base + Tax */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Base ({gastoEnUSD ? "USD" : "MXN"})</Label>
              <Input
                type="number" inputMode="decimal" step="0.01" value={base}
                onChange={(e) => setBase(e.target.value)}
                className="h-9"
                placeholder="0.00"
                data-testid="calc-quick-base"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Tax ({gastoEnUSD ? "USD" : "MXN"})</Label>
              <Input
                type="number" inputMode="decimal" step="0.01" value={tax}
                onChange={(e) => setTax(e.target.value)}
                className="h-9"
                placeholder="0.00"
                data-testid="calc-quick-tax"
              />
            </div>
          </div>

          {/* TC proveedor solo si USD */}
          {gastoEnUSD && (
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">TC proveedor</Label>
              <Input
                type="number" inputMode="decimal" step="0.0001" value={tcProveedor}
                onChange={(e) => setTcProveedor(e.target.value)}
                className="h-9"
                placeholder={tcSistema.usd ? String(tcSistema.usd) : "0.0000"}
                data-testid="calc-quick-tc"
              />
            </div>
          )}

          {/* Canal + comisión */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Canal</Label>
              <Select value={canal} onValueChange={setCanal}>
                <SelectTrigger className="h-9" data-testid="calc-quick-canal"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CANALES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center justify-between">
                <span>Comisión %</span>
                <span className="flex items-center gap-1 text-[9px] normal-case">
                  <Switch className="scale-75" checked={autoComision} onCheckedChange={setAutoComision} />
                  manual
                </span>
              </Label>
              <Input
                type="number" inputMode="decimal" step="0.1"
                value={comision}
                onChange={(e) => setComision(e.target.value)}
                disabled={!autoComision}
                className="h-9"
                data-testid="calc-quick-comision"
              />
            </div>
          </div>

          {/* Forma de pago */}
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Forma de pago</Label>
            <Select value={pago} onValueChange={setPago}>
              <SelectTrigger className="h-9" data-testid="calc-quick-pago"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAGOS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Resultado */}
          <div className="rounded-lg border border-cyan-200 bg-gradient-to-br from-cyan-50 to-blue-50 p-3 space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-600">Total gasto</span>
              <span className="font-semibold text-slate-800">${fmt(totalGastoMXN)} MXN</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-600">PVP (venta)</span>
              <span className="font-semibold text-[#0037FF]">${fmt(pvpMXN)} MXN</span>
            </div>
            <div className="border-t border-cyan-200 pt-1.5 flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-700">Total a pagar ({PAGOS.find((p) => p.key === pago)?.label})</span>
              <span className="text-lg font-bold text-[#00157F]" data-testid="calc-quick-total">${fmt(totalPago)}</span>
            </div>
          </div>

          {/* Reset */}
          <Button
            variant="outline"
            size="sm"
            onClick={reset}
            className="w-full h-8 text-xs border-slate-200"
            data-testid="calc-quick-reset"
          >
            <RotateCcw className="w-3 h-3 mr-1.5" /> Limpiar
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
